import React, { useEffect, useState } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import VpnLockOutlinedIcon from "@material-ui/icons/VpnLockOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import {
  buildWhatsappProxyLine,
  parseWhatsappProxyLine,
} from "../../utils/parseWhatsappProxy";

const DEFAULT_TYPE = "http";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  section: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  fullWidth: {
    gridColumn: "1 / -1",
  },
  statusRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  actions: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
    flexWrap: "wrap",
  },
  checkResult: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
  },
  note: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(2),
    fontSize: "0.9rem",
  },
  formatHint: {
    fontFamily: "monospace",
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
  },
  parsedFields: {
    marginTop: theme.spacing(1),
  },
}));

const applyParsedProxy = (parsed, setters) => {
  if (!parsed) {
    return false;
  }

  setters.setHost(parsed.host);
  setters.setPort(parsed.port);
  setters.setUsername(parsed.username);
  setters.setPassword(parsed.password);
  setters.setType(parsed.type || DEFAULT_TYPE);
  return true;
};

const Proxy = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [rawLine, setRawLine] = useState("");
  const [host, setHost] = useState("");
  const [port, setPort] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [type, setType] = useState(DEFAULT_TYPE);
  const [source, setSource] = useState("none");
  const [checkResult, setCheckResult] = useState(null);

  const setters = { setHost, setPort, setUsername, setPassword, setType };

  const syncRawLineFromFields = (nextFields) => {
    const line = buildWhatsappProxyLine({
      host: nextFields.host,
      port: nextFields.port,
      username: nextFields.username,
      password: nextFields.password,
      type: nextFields.type,
    });
    setRawLine(line);
    return line;
  };

  const buildPayload = () => ({
    enabled,
    rawValue: rawLine.trim() || syncRawLineFromFields({ host, port, username, password, type }),
    host,
    port: port ? Number(port) : undefined,
    username,
    password: password || undefined,
    type,
  });

  const handleRawLineChange = (value) => {
    setRawLine(value);

    const parsed = parseWhatsappProxyLine(value, DEFAULT_TYPE);
    if (parsed) {
      applyParsedProxy(parsed, setters);
      setEnabled(true);
    }
  };

  const fetchProxy = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp-proxy");
      const nextEnabled = Boolean(data.enabled);
      const nextHost = data.host || "";
      const nextPort = data.port ? String(data.port) : "";
      const nextUsername = data.username || "";
      const nextType = data.type || DEFAULT_TYPE;
      const nextRaw =
        data.rawValue ||
        buildWhatsappProxyLine({
          host: nextHost,
          port: nextPort,
          username: nextUsername,
          password: "",
          type: nextType,
        });

      setEnabled(nextEnabled);
      setRawLine(nextRaw);
      setHost(nextHost);
      setPort(nextPort);
      setUsername(nextUsername);
      setPassword("");
      setType(nextType);
      setSource(data.source || "none");
      setCheckResult(null);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProxy();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/whatsapp-proxy", buildPayload());
      setEnabled(Boolean(data.enabled));
      setHost(data.host || "");
      setPort(data.port ? String(data.port) : "");
      setUsername(data.username || "");
      setPassword("");
      setType(data.type || DEFAULT_TYPE);
      setRawLine(
        data.rawValue ||
          buildWhatsappProxyLine({
            host: data.host,
            port: data.port,
            username: data.username,
            password: "",
            type: data.type || DEFAULT_TYPE,
          })
      );
      setSource(data.source || "none");
      toast.success(i18n.t("proxy.toasts.saved"));
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleCheck = async () => {
    setChecking(true);
    setCheckResult(null);
    try {
      const { data } = await api.post("/whatsapp-proxy/check", buildPayload());
      setCheckResult(data);
      if (data.ok) {
        toast.success(i18n.t("proxy.toasts.checkOk"));
      } else {
        toast.error(data.error || i18n.t("proxy.toasts.checkFail"));
      }
    } catch (err) {
      toastError(err);
    } finally {
      setChecking(false);
    }
  };

  const sourceLabel =
    source === "settings"
      ? i18n.t("proxy.source.settings")
      : source === "env"
      ? i18n.t("proxy.source.env")
      : i18n.t("proxy.source.none");

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("proxy.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            onClick={fetchProxy}
            disabled={loading}
          >
            {i18n.t("proxy.buttons.reload")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {loading ? (
          <CircularProgress />
        ) : (
          <>
            <div className={classes.statusRow}>
              <VpnLockOutlinedIcon color={enabled ? "primary" : "disabled"} />
              <Chip
                size="small"
                color={enabled ? "primary" : "default"}
                label={
                  enabled
                    ? i18n.t("proxy.status.enabled")
                    : i18n.t("proxy.status.disabled")
                }
              />
              <Chip size="small" variant="outlined" label={sourceLabel} />
              <Chip size="small" variant="outlined" label="HTTP" />
            </div>

            <FormControlLabel
              className={classes.fullWidth}
              control={
                <Switch
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label={i18n.t("proxy.fields.enabled")}
            />

            <TextField
              className={classes.fullWidth}
              label={i18n.t("proxy.fields.rawLine")}
              value={rawLine}
              onChange={(e) => handleRawLineChange(e.target.value)}
              variant="outlined"
              fullWidth
              disabled={!enabled}
              placeholder="185.14.238.23:9999:usuario:senha"
              helperText={i18n.t("proxy.formatHint")}
              InputProps={{
                style: { fontFamily: "monospace" },
              }}
            />

            <Typography variant="subtitle2" className={classes.parsedFields}>
              {i18n.t("proxy.fields.parsedTitle")}
            </Typography>

            <div className={classes.section}>
              <TextField
                label={i18n.t("proxy.fields.host")}
                value={host}
                onChange={(e) => {
                  const nextHost = e.target.value;
                  setHost(nextHost);
                  syncRawLineFromFields({
                    host: nextHost,
                    port,
                    username,
                    password,
                    type,
                  });
                }}
                variant="outlined"
                fullWidth
                disabled={!enabled}
              />
              <TextField
                label={i18n.t("proxy.fields.port")}
                value={port}
                onChange={(e) => {
                  const nextPort = e.target.value.replace(/\D/g, "");
                  setPort(nextPort);
                  syncRawLineFromFields({
                    host,
                    port: nextPort,
                    username,
                    password,
                    type,
                  });
                }}
                variant="outlined"
                fullWidth
                disabled={!enabled}
                type="number"
              />
              <TextField
                label={i18n.t("proxy.fields.username")}
                value={username}
                onChange={(e) => {
                  const nextUsername = e.target.value;
                  setUsername(nextUsername);
                  syncRawLineFromFields({
                    host,
                    port,
                    username: nextUsername,
                    password,
                    type,
                  });
                }}
                variant="outlined"
                fullWidth
                disabled={!enabled}
              />
              <TextField
                label={i18n.t("proxy.fields.password")}
                value={password}
                onChange={(e) => {
                  const nextPassword = e.target.value;
                  setPassword(nextPassword);
                  syncRawLineFromFields({
                    host,
                    port,
                    username,
                    password: nextPassword,
                    type,
                  });
                }}
                variant="outlined"
                fullWidth
                disabled={!enabled}
                type="password"
                placeholder={i18n.t("proxy.fields.passwordKeep")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <VpnLockOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl variant="outlined" fullWidth disabled={!enabled}>
                <InputLabel>{i18n.t("proxy.fields.type")}</InputLabel>
                <Select
                  value={type}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    setType(nextType);
                    syncRawLineFromFields({
                      host,
                      port,
                      username,
                      password,
                      type: nextType,
                    });
                  }}
                  label={i18n.t("proxy.fields.type")}
                >
                  <MenuItem value="http">HTTP</MenuItem>
                  <MenuItem value="socks5">SOCKS5</MenuItem>
                  <MenuItem value="socks4">SOCKS4</MenuItem>
                </Select>
              </FormControl>
            </div>

            <div className={classes.actions}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  i18n.t("proxy.buttons.save")
                )}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCheck}
                disabled={checking || !enabled}
              >
                {checking ? (
                  <CircularProgress size={22} />
                ) : (
                  i18n.t("proxy.buttons.check")
                )}
              </Button>
            </div>

            {checkResult && (
              <div className={classes.checkResult}>
                <Typography variant="subtitle2">
                  {checkResult.ok ? (
                    <>
                      <CheckCircleOutlineIcon
                        fontSize="small"
                        color="primary"
                        style={{ verticalAlign: "middle", marginRight: 8 }}
                      />
                      {i18n.t("proxy.check.ok")}
                    </>
                  ) : (
                    <>
                      <ErrorOutlineIcon
                        fontSize="small"
                        color="error"
                        style={{ verticalAlign: "middle", marginRight: 8 }}
                      />
                      {i18n.t("proxy.check.fail")}
                    </>
                  )}
                </Typography>
                {checkResult.ok && (
                  <>
                    <Typography variant="body2">
                      {i18n.t("proxy.check.latency", {
                        ms: checkResult.latencyMs,
                      })}
                    </Typography>
                    <Typography variant="body2">
                      {i18n.t("proxy.check.ip", { ip: checkResult.externalIp })}
                    </Typography>
                  </>
                )}
                {!checkResult.ok && checkResult.error && (
                  <Typography variant="body2" color="error">
                    {checkResult.error}
                  </Typography>
                )}
              </div>
            )}

            <Typography className={classes.note}>
              {i18n.t("proxy.note")}
            </Typography>
          </>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Proxy;
